import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { SITE } from "../data/site";
import { Quote } from "lucide-react";

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="bg-[var(--bg-primary)]">
      <Navbar />

      <section className="pt-36 pb-16 bg-[var(--maroon-950)] text-white relative grain">
        <div className="container-edge relative grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="overline text-[#D38888]">About the firm</div>
            <h1 className="font-display text-5xl md:text-7xl mt-5 leading-[1] tracking-tight">
              Patiala-made, since {SITE.established}.
            </h1>
          </div>
          <div className="md:col-span-5 md:pb-3">
            <p className="text-[#E8DCC8]/85 text-lg leading-relaxed">
              A proprietorship workshop with a fifteen-year track record of supplying dies, rollers and bakery tooling
              to some of India's most demanding biscuit lines.
            </p>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="section">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5 md:sticky md:top-28">
            <img
              data-testid="about-image"
              src="/media/pew-3.jpeg"
              alt="Workshop floor at Patiala Engineering Works"
              className="w-full aspect-[4/5] object-cover border border-[var(--border-subtle)]"
            />
          </div>
          <div className="md:col-span-7 space-y-6">
            <div className="overline">Our story</div>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--maroon-950)] leading-tight">
              From a small unit on Focal Area to lines across India.
            </h2>
            <p className="text-lg text-[var(--text-secondary)] leading-relaxed">
              Patiala Engineering Works was founded with a single belief — that the humble biscuit die deserves the same
              obsession as the products it stamps. What began as a small unit on Focal Area, Patiala has grown into a
              specialist tooling shop trusted by bakeries, dairies and confectioneries across India.
            </p>
            <p className="text-lg text-[var(--text-secondary)] leading-relaxed">
              Our team — small by design — combines decades of hand metalwork with modern CNC engraving and finishing.
              Every die is checked by eye and by gauge before it leaves the shop.
            </p>

            <div className="bg-[var(--bg-secondary)] border-l-4 border-[var(--maroon-700)] p-6 md:p-8 mt-8">
              <Quote className="text-[var(--maroon-700)]" size={28} />
              <p className="font-display text-xl md:text-2xl mt-4 text-[var(--maroon-950)] leading-snug">
                "A die runs for years. The bakery remembers the day it was made. We make sure they remember it well."
              </p>
              <div className="mt-5 font-mono-accent text-[11px] tracking-[0.22em] uppercase text-[var(--maroon-700)]">
                {SITE.ceo} · Proprietor & CEO
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FACTSHEET */}
      <section className="section bg-[var(--bg-secondary)]">
        <div className="container-edge">
          <div className="overline">Factsheet</div>
          <h2 className="font-display text-4xl md:text-5xl mt-5 text-[var(--maroon-950)] leading-tight">
            By the numbers.
          </h2>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-subtle)] border border-[var(--border-subtle)]">
            {[
              { k: "Nature of Business", v: "Manufacturer & Service Provider" },
              { k: "Year Established", v: SITE.established.toString() },
              { k: "Legal Status", v: "Proprietorship" },
              { k: "Company CEO", v: SITE.ceo },
              { k: "Team Size", v: "Up to 10 craftsmen" },
              { k: "Industries Served", v: "Bakery, Dairy, Confectionery" },
              { k: "GST Number", v: SITE.gst },
              { k: "Import-Export Code", v: SITE.iec },
            ].map((f) => (
              <div key={f.k} className="bg-[var(--bg-primary)] p-7">
                <div className="font-mono-accent text-[11px] tracking-[0.2em] uppercase text-[var(--maroon-700)]">{f.k}</div>
                <div className="font-display text-xl md:text-2xl mt-3 text-[var(--maroon-950)] leading-snug">{f.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE TEAM */}
      <section className="section">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="overline">Core team</div>
            <h2 className="font-display text-4xl md:text-5xl mt-5 text-[var(--maroon-950)] leading-tight">
              Ten hands. <em className="not-italic text-[var(--maroon-700)]">One bench.</em> Decades of know-how.
            </h2>
            <p className="mt-6 text-lg text-[var(--text-secondary)] leading-relaxed">
              Our shop is intentionally small. A core team of up to ten craftsmen — toolmakers, machinists and finishers — works
              alongside our proprietor on every order. Drawings are reviewed by hand, dies are checked by eye and gauge, and
              every piece is signed off before it ships.
            </p>
            <p className="mt-5 text-lg text-[var(--text-secondary)] leading-relaxed">
              That's how a fifteen-year-old workshop earns repeat business from some of India's most demanding bakery floors —
              one die, one customer, one delivery at a time.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[var(--border-subtle)] border border-[var(--border-subtle)] max-w-xl">
              {[
                { k: "Toolmakers", v: "On-bench" },
                { k: "CNC operators", v: "In-house" },
                { k: "Finishing", v: "Hand-led" },
              ].map((c) => (
                <div key={c.k} className="bg-[var(--bg-primary)] p-5">
                  <div className="font-display text-xl text-[var(--maroon-950)]">{c.v}</div>
                  <div className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[var(--maroon-700)] mt-1">{c.k}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5 order-1 md:order-2">
            <div className="relative">
              <img
                data-testid="about-team-image"
                src="/media/pew-1.jpeg"
                alt="Craftsman at the workstation in our Patiala workshop"
                className="w-full aspect-[4/5] object-cover border border-[var(--border-subtle)]"
              />
              <div className="absolute -bottom-4 -left-4 bg-[var(--maroon-700)] text-[#FDFBF7] px-5 py-3 hidden md:block">
                <div className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#E8DCC8]">On the bench</div>
                <div className="font-display text-lg leading-none mt-1">Patiala, Punjab</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="section">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline">Industries we serve</div>
            <h2 className="font-display text-4xl md:text-5xl mt-5 text-[var(--maroon-950)] leading-tight">
              Wherever a belt moves and a die stamps.
            </h2>
          </div>
          <div className="md:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--border-subtle)] border border-[var(--border-subtle)]">
              {SITE.industries.map((ind) => (
                <li key={ind} className="bg-[var(--bg-primary)] px-6 py-7 font-display text-2xl text-[var(--maroon-950)]">
                  {ind}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
